import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@material-tailwind/react";
import "./PopPup.css";
import React, { useEffect, useState } from "react";
import { BASE_URL } from "server";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface ContactSupportPopPupProps {
  onClose: () => void;
  onConversationCreated?: (conversation: { id: string; title: string }) => void;
  userRole?: string;
}

const ContactSupportPopPup: React.FC<ContactSupportPopPupProps> = ({
  onClose,
  onConversationCreated,
  userRole = "student",
}) => {
  const [showPopup, setShowPopup] = useState(true);
  const [animateObject, setAnimateObject] = useState(false);
  const [animateDescription, setAnimateDescription] = useState(false);
  const [objectText, setObjectText] = useState<string>("");
  const [descriptionText, setDescriptionText] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ObjectInput = React.useRef<HTMLTextAreaElement>(null);
  const DescriptionInput = React.useRef<HTMLTextAreaElement>(null);

  const clearInput = () => {
    if (ObjectInput.current) {
      ObjectInput.current.value = "";
    }
    if (DescriptionInput.current) {
      DescriptionInput.current.value = "";
    }
  };

  const handleSendMessage = async () => {
    if (objectText === "" || descriptionText === "") {
      if (objectText === "") {
        setAnimateObject(true);
      }
      if (descriptionText === "") {
        setAnimateDescription(true);
      }
      setTimeout(() => {
        setAnimateObject(false);
        setAnimateDescription(false);
      }, 1000);
      return;
    }

    setIsSubmitting(true);
    try {
      const newConversation = await createSupportConversation(
        objectText,
        descriptionText,
      );
      if (newConversation && onConversationCreated) {
        onConversationCreated(newConversation);
      }
      clearInput();
      setObjectText("");
      setDescriptionText("");
      setTimeout(() => {
        handleClose();
      }, 500);
    } catch (error) {
      console.error("Erreur lors de la création:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowPopup(false);
    onClose();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSendMessage();
    }
  };

  return (
    <>
      {showPopup && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Dialog
            open={showPopup}
            handler={handleClose}
            size="sm"
            className="w-full max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden"
            style={{ zIndex: 9999 }}
          >
            {/* Header */}
            <DialogHeader className="flex items-center justify-between p-6 pb-4 border-b border-gray-100">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Nouvelle conversation
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Contactez l'équipe support
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </DialogHeader>

            {/* Body */}
            <DialogBody className="p-6 space-y-6">
              {/* Champ Objet */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Objet <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 ${
                    animateObject ? "shake border-red-300" : ""
                  }`}
                  ref={ObjectInput}
                  value={objectText}
                  onChange={(e) => setObjectText(e.target.value)}
                  rows={3}
                  placeholder="Résumez votre problème en quelques mots..."
                  onKeyPress={handleKeyPress}
                />
                {animateObject && (
                  <p className="text-sm text-red-500">L'objet est requis</p>
                )}
              </div>

              {/* Champ Description */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  className={`w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 ${
                    animateDescription ? "shake border-red-300" : ""
                  }`}
                  value={descriptionText}
                  onChange={(e) => setDescriptionText(e.target.value)}
                  ref={DescriptionInput}
                  rows={5}
                  placeholder="Décrivez votre problème en détail..."
                  onKeyPress={handleKeyPress}
                />
                {animateDescription && (
                  <p className="text-sm text-red-500">
                    La description est requise
                  </p>
                )}
              </div>
            </DialogBody>

            {/* Footer */}
            <DialogFooter className="flex items-center justify-between p-6 pt-4 border-t border-gray-100 bg-gray-50">
              <Button
                onClick={() => {
                  clearInput();
                  setObjectText("");
                  setDescriptionText("");
                }}
                variant="text"
                className="text-gray-600 hover:text-gray-800 transition-colors duration-200"
              >
                Effacer
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSendMessage}
                  disabled={
                    isSubmitting ||
                    !objectText.trim() ||
                    !descriptionText.trim()
                  }
                  className={`bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Envoi...
                    </div>
                  ) : (
                    "Envoyer"
                  )}
                </Button>
              </div>
            </DialogFooter>
          </Dialog>
        </div>
      )}
    </>
  );
};

export default ContactSupportPopPup;

async function createSupportConversation(
  title: string,
  body: string,
): Promise<{ id: string; title: string } | null> {
  const url = `${BASE_URL}/conversations/support/create/from-user`;
  const headers = {
    accept: "application/json",
    "Content-Type": "application/json",
  };
  const data = {
    conversationData: {
      title: title,
      isActivated: true,
    },
    messageData: {
      body: body,
    },
  };

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      return null;
    }

    const result = await response.json();
    return {
      id: result.conversationId || result._id,
      title: title,
    };
  } catch (error) {
    console.log("Erreur lors de la création de la conversation:", error);
    return null;
  }
}
